import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getData, deleteData } from "../../utils/awsService";
import "./AdminPanel.css";

const CouplePanel = () => {
  const [consultations, setConsultations] = useState([]);
  const [filteredConsultations, setFilteredConsultations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    const fetchConsultations = async () => {
      try {
        setLoading(true);
        setError(null);

        const responseData = await getData("/couple");

        // Extract consultation data
        let consultationData = responseData.data["data"];

        // Transform data to extract specific fields
        const processedConsultations = consultationData.map((consultation) => ({
          // Separate extraction of key fields
          id: consultation.id || null,
          name: consultation.name || "Unknown",
          age: consultation.age || "N/A",
          timestamp: consultation.timestamp || consultation.createdAt || new Date().toISOString(),

          // Optional: Include other relevant fields if needed
          email: consultation.email || "",
          supportReason: consultation.supportReason || "",
          city: consultation.city || "",
          phone: consultation.contactNo || "N/A", // Added phone field
        }));
        const sortedConsultations = processedConsultations.sort((a, b) => 
          new Date(b.timestamp) - new Date(a.timestamp)
        );
        setConsultations(sortedConsultations);
        setFilteredConsultations(sortedConsultations);
      } catch (error) {
        console.error("Error in fetching consultations:", error);
        setError(error.message || "Failed to fetch consultations");
        setConsultations([]);
        setFilteredConsultations([]);
      } finally {
        setLoading(false);
      }
    };

    fetchConsultations();
    const intervalId = setInterval(fetchConsultations, 30000);

    return () => clearInterval(intervalId);
  }, []);

  // Handle search functionality
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredConsultations(consultations);
      return;
    }

    const lowercasedSearch = searchTerm.toLowerCase();
    const filtered = consultations.filter(
      (consultation) =>
        consultation.name.toLowerCase().includes(lowercasedSearch) ||
        (consultation.phone && consultation.phone.includes(searchTerm))
    );
    
    setFilteredConsultations(filtered);
  }, [searchTerm, consultations]);

  const handleView = (id) => {
    navigate(`/admin/responses/couple-responses/${id}`);
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this consultation?")) {
      try {
        await deleteData("/couple", id);

        setConsultations((prevConsultations) =>
          prevConsultations.filter((consultation) => consultation.id !== id)
        );
        setFilteredConsultations((prevFiltered) =>
          prevFiltered.filter((consultation) => consultation.id !== id)
        );
      } catch (error) {
        console.error("Error deleting consultation:", error);
        alert("Failed to delete consultation.");
      }
    }
  };
  
  // Format date for display
  const formatDateTime = (timestamp) => {
    try {
      const date = new Date(timestamp);
      return date.toLocaleString();
    } catch (error) {
      return "Unknown Date";
    }
  };
  
  if (loading) {
    return <div className="loading-container">Loading consultations...</div>;
  }

  if (error) {
    return (
      <div className="error-container">
        <p>Error: {error}</p>
        <button onClick={() => window.location.reload()}>Retry</button>
      </div>
    );
  }
  
  const handleBack = () => {
    navigate("/admin/admin-dashboard");
  };

  return (
    <div className="admin-container">
      <button onClick={handleBack} className="back-btn">
        ← Back to Dashboard
      </button>

      <h1 className="admin-title">Couples Consultations Admin Panel</h1>

      {/* Search bar */}
      <div className="search-container">
        <input
          type="text"
          placeholder="Search by name or phone number..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
        {searchTerm && (
          <button 
            className="search-clear" 
            onClick={() => setSearchTerm("")}
            aria-label="Clear search"
          >
            ×
          </button>
        )}
      </div>

      {filteredConsultations.length === 0 ? (
        <div className="no-data-container">
          {searchTerm ? (
            <p className="no-data">No matching consultations found.</p>
          ) : (
            <p className="no-data">No consultations found.</p>
          )}
          <button
            onClick={() => window.location.reload()}
            className="retry-btn"
          >
            Reload Data
          </button>
        </div>
      ) : (
        <div className="consultations-grid">
          {filteredConsultations.map((consultation) => (
            <div key={consultation.id} className="consultation-card">
              <div className="card-header">
                <h3 className="client-name">{consultation.name}</h3>
                <div className="timestamp">
                  Date:{" "}
                  {(() => {
                    const date = new Date(consultation.timestamp);
                    const year = date.getFullYear();
                    const month = (date.getMonth() + 1)
                      .toString()
                      .padStart(2, "0");
                    const day = date.getDate().toString().padStart(2, "0");
                    return `${year}-${month}-${day}`;
                  })()}
                  <br />
                  Time:{" "}
                  {(() => {
                    const date = new Date(consultation.timestamp);
                    let hour = date.getHours();
                    const minutes = date
                      .getMinutes()
                      .toString()
                      .padStart(2, "0");
                    const ampm = hour < 12 ? "AM" : "PM";
                    hour = hour % 12 || 12;
                    return `${hour
                      .toString()
                      .padStart(2, "0")}:${minutes} ${ampm}`;
                  })()}
                </div>
              </div>
              <div className="card-content">
                <p>Email: {consultation.email}</p>
                <p>Phone: {consultation.phone}</p>
              </div>
              <div className="card-actions">
                <button
                  onClick={() => handleView(consultation.id)}
                  className="view-btn"
                >
                  View Details
                </button>
                <button
                  onClick={() => handleDelete(consultation.id)}
                  className="delete-btn"
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CouplePanel;